import { useCallback, useState } from "react";

import { validateFile } from "../utils/validation";

export interface UseExcelDropzoneOptions {
  onFileDrop?: (file: File) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
}

export interface UseExcelDropzoneReturn {
  isDragging: boolean;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
}

/**
 * Custom hook for handling drag-and-drop Excel file uploads
 * Provides a clean separation of drag-and-drop logic from UI components
 */
export const useExcelDropzone = ({
  onFileDrop,
  onError,
  disabled = false
}: UseExcelDropzoneOptions): UseExcelDropzoneReturn => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;

      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;

      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;

      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];

      if (!file) {
        onError?.("No file was dropped");
        return;
      }

      // Validate file
      const validation = validateFile(file);
      if (!validation.isValid) {
        onError?.(validation.errors.join(". "));
        return;
      }

      // File is valid, trigger callback
      onFileDrop?.(file);
    },
    [disabled, onFileDrop, onError]
  );

  return {
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop
  };
};
